import React, { useEffect, useState } from 'react';

import {
    ValidateVendorCodesInput,
    ValidateVendorCodesOutput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Input from '@amzn/meridian/input';
import Row from '@amzn/meridian/row';
import Textarea from '@amzn/meridian/textarea';

import { cloneDeep, isEqual } from 'lodash';
import { contractConfigurationClient } from 'src/api/client';
import { ALL_SERVICE, GLOBAL_TERRITORY } from 'src/constants/context';
import {
    AT_LEAST_3_CHAR,
    AT_MOST_100_CHAR,
    AT_MOST_255_CHAR,
    DATE_RANGE_OVERLAP,
    END_DATE_AFTER_START_DATE,
    EXACTLY_4_CHAR,
    EXACTLY_4_TO_5_CHAR,
    FIRST_OF_MONTH_DATE,
    INCOMPLETE_DATE,
    INVALID_DATE,
    IS_REQUIRED,
    LAST_OF_MONTH_DATE,
    MUST_START_WITH_ZQ,
    START_DATE_BEFORE_END_DATE
} from 'src/constants/errorMessages';
import { AppContext } from 'src/contexts/appContext';
import { initialAddVendorState, newVendorCode, newVendorCodeOverride } from 'src/data/AddVendorData';
import { IVendor, IVendorCode, IVendorCodeOverride } from 'src/types/IVendor';
import { AndonCord } from 'src/util/andon-cord/andon-cord';
import { getDateAndTimestamp, getLastDayOfMonth } from 'src/util/dateTimeUtil';
import { emptyDateYyyyMmDdMaskStr } from 'src/util/inputMask';
import {
    validateDateString,
    validateExactLength,
    validateMaxLength,
    validateMinLength,
    validateRequired
} from 'src/util/validationsUtil';

import { SubmissionSettings } from '../SubmissionSettings';
import { mapVendorCodeOverrides, mapVendorCodes } from '../mappers/mapVendorCodes';
import './AddNewVendor.scss';
import { VendorCodeOverrides } from './VendorCodeOverrides';
import { VendorCodes } from './VendorCodes';

interface IAddNewVendorProps {
    vendor: IVendor;
    setVendor: React.Dispatch<React.SetStateAction<IVendor>>;
    isEditMode: boolean;
    onNext: () => void;
    openCancelModal: () => void;
}

export const AddNewVendor: React.FC<IAddNewVendorProps> = ({
    vendor,
    setVendor,
    isEditMode,
    onNext,
    openCancelModal
}: IAddNewVendorProps) => {
    const VENDOR_CODES = 'vendorCodes';
    const VENDOR_CODE_OVERRIDES = 'vendorCodeOverrides';
    const MAPPING_START = 'mappingStart';

    const [errorMessages, setErrorMessages] = useState<Record<string, string>>({});
    const [isFormValid, setIsFormValid] = useState(false);
    const { metadata, setTopAlertMsg } = React.useContext(AppContext);
    const services = Object.values(metadata.serviceMapping).filter((service) => service.serviceId !== ALL_SERVICE.ID);
    const territories = Object.values(metadata.territoryMapping).filter(
        (territory) => territory.territoryId !== GLOBAL_TERRITORY.ID
    );

    useEffect(() => {
        setIsFormValid(checkFormValidity());
    }, [vendor, errorMessages]);

    useEffect(() => {
        checkCodeDuplicates(false);
        checkCodeDuplicates(true);
        checkOverrideCodeDateOverlaps();
    }, [vendor.vendorCodes.length, vendor.vendorCodeOverrides.length]);

    const handleChange = (field: string, value: string | boolean) => {
        setVendor((prevState) => ({
            ...prevState,
            [field]: value
        }));
    };

    const handleInvalidVendorCodes = (invalidVendorCodes: ValidateVendorCodesOutput) => {
        const setVendorCodeErrorMessage = (isOverride: boolean, id: string, field: string) => {
            setErrorMessages((prevState) => {
                const newState = { ...prevState };
                newState[`${isOverride ? VENDOR_CODE_OVERRIDES : VENDOR_CODES}${id}${field}`] =
                    'Code must be unique to this vendor';
                return newState;
            });
        };

        vendor.vendorCodes.forEach((vendorCode) => {
            if (invalidVendorCodes.invalidCatalogVendorCodesList?.includes(vendorCode.catalogCode)) {
                setVendorCodeErrorMessage(false, vendorCode.vendorCodeId, 'catalogCode');
            }
            if (invalidVendorCodes.invalidRoyaltyVendorCodesList?.includes(vendorCode.royaltyCode)) {
                setVendorCodeErrorMessage(false, vendorCode.vendorCodeId, 'royaltyCode');
            }
        });

        vendor.vendorCodeOverrides.forEach((vendorCodeOverride) => {
            if (invalidVendorCodes.invalidRoyaltyVendorCodesList?.includes(vendorCodeOverride.royaltyCode)) {
                setVendorCodeErrorMessage(true, vendorCodeOverride.vendorCodeId, 'royaltyCode');
            }
        });
    };

    const handleNext = () => {
        let input: ValidateVendorCodesInput = {
            defaultVendorCodes: mapVendorCodes(vendor.vendorCodes),
            overrideVendorCodes: mapVendorCodeOverrides(vendor.vendorCodeOverrides, services, territories),
            submitAccruals: vendor.submitAccruals,
            submitPayments: vendor.submitPayments
        };

        if (vendor.vendorId) {
            input = { ...input, entityId: parseInt(vendor.vendorId) };
        }

        contractConfigurationClient.validateVendorCodes(ValidateVendorCodesInput.clone(input)).subscribe(
            (response: ValidateVendorCodesOutput) => {
                handleInvalidVendorCodes(response);

                if (
                    isFormValid &&
                    !response.invalidCatalogVendorCodesList?.length &&
                    !response.invalidRoyaltyVendorCodesList?.length
                ) {
                    onNext();
                }
            },
            (error: UnknownCoralError) => {
                // eslint-disable-next-line no-console
                console.error(error.receivedException.message);
                setTopAlertMsg(error.receivedException.message);
            }
        );
    };

    const addVendorCode = (isOverride = false) => {
        const stateField = isOverride ? VENDOR_CODE_OVERRIDES : VENDOR_CODES;
        const vendorCode = isOverride ? newVendorCodeOverride() : newVendorCode();
        setVendor((prevState) => ({
            ...prevState,
            [stateField]: [...prevState[stateField], vendorCode]
        }));
    };

    const removeVendorCode = (vendorCodeId: string, isOverride = false) => {
        const stateField = isOverride ? VENDOR_CODE_OVERRIDES : VENDOR_CODES;
        setVendor((prevState) => {
            const newState = cloneDeep(prevState);
            const index = newState[stateField].findIndex((vendorCode) => vendorCode.vendorCodeId === vendorCodeId);
            newState[stateField].splice(index, 1);
            return newState;
        });
        setErrorMessages((prevState) => {
            const newState = { ...prevState };
            for (const field in newState) {
                if (field.includes(`${stateField}${vendorCodeId}`)) {
                    delete newState[field];
                }
            }
            return newState;
        });
    };

    const updateVendorCode = (
        vendorCode: IVendorCode,
        value: string,
        field: string,
        vendorCodeId: string,
        isOverride = false
    ) => {
        const stateField = isOverride ? VENDOR_CODE_OVERRIDES : VENDOR_CODES;
        const updatedVendorCode: IVendorCode = {
            ...vendorCode,
            [field]: value
        };
        setVendor((prevState) => {
            const newState = cloneDeep(prevState);
            const index = newState[stateField].findIndex((vendorCode) => vendorCode.vendorCodeId === vendorCodeId);
            newState[stateField][index] = updatedVendorCode;
            return newState;
        });
    };

    const checkFormValidity = () => {
        if (!vendor.vendorName) return false;

        if (!vendor.description) return false;

        const requiredVendorCodeFields = new Set([
            'catalogCode',
            'royaltyCode',
            'mappingStart',
            'serviceId',
            'territoryId'
        ]);
        for (let i = 0; i < vendor.vendorCodes.length; i++) {
            for (const field in vendor.vendorCodes[i]) {
                if (requiredVendorCodeFields.has(field)) {
                    if (!vendor.vendorCodes[i][field as keyof IVendorCode]) {
                        return false;
                    }
                }
            }
        }
        for (let i = 0; i < vendor.vendorCodeOverrides.length; i++) {
            for (const field in vendor.vendorCodeOverrides[i]) {
                if (requiredVendorCodeFields.has(field)) {
                    if (vendor.vendorCodeOverrides[i][field as keyof IVendorCodeOverride] === undefined) {
                        return false;
                    }
                }
            }
        }

        return isEqual(errorMessages, {}) && !isEqual(vendor, initialAddVendorState);
    };

    const checkCodeDuplicates = (isRoyaltyCode = false) => {
        const errorMessage = `${isRoyaltyCode ? 'Royalty' : 'Catalog'} code must be unique`;
        const codeField = isRoyaltyCode ? 'royaltyCode' : 'catalogCode';
        let duplicatesFound = false;

        const codesMap: Record<string, string[]> = {};
        const vendorCodes = [...vendor.vendorCodes];
        vendorCodes.forEach((vendorCode) => {
            const code = vendorCode[codeField];
            if (codesMap[code]) {
                codesMap[code].push(vendorCode.vendorCodeId);
            } else if (validateMinLength(code, 4)) {
                codesMap[code] = [vendorCode.vendorCodeId];
            }
        });

        for (const key in codesMap) {
            if (codesMap[key].length > 1) {
                duplicatesFound = true;
                setErrorMessages((prevState) => {
                    const newState = { ...prevState };
                    codesMap[key].forEach((vendorCodeId) => {
                        newState[`${VENDOR_CODES}${vendorCodeId}${codeField}`] = errorMessage;
                    });
                    return newState;
                });
            } else if (errorMessages[`${VENDOR_CODES}${codesMap[key][0]}${codeField}`] === errorMessage) {
                setErrorMessages((prevState) => {
                    const newState = { ...prevState };
                    delete newState[`${VENDOR_CODES}${codesMap[key][0]}${codeField}`];
                    return newState;
                });
            }
        }

        return duplicatesFound;
    };

    const validateField = (field: string, vendorCodeId: string | null = null, isOverride = false) => {
        let errorMessage = '';
        const vendorCodeField = isOverride ? VENDOR_CODE_OVERRIDES : VENDOR_CODES;
        const index = vendor[vendorCodeField].findIndex((vendorCode) => vendorCode.vendorCodeId === vendorCodeId);

        if (field === 'vendorName') {
            if (!validateRequired(vendor[field])) {
                errorMessage = IS_REQUIRED;
            } else if (!validateMinLength(vendor[field], 3)) {
                errorMessage = AT_LEAST_3_CHAR;
            } else if (!validateMaxLength(vendor[field], 100)) {
                errorMessage = AT_MOST_100_CHAR;
            }
        }

        if (field === 'description') {
            if (!validateRequired(vendor[field])) {
                errorMessage = IS_REQUIRED;
            } else if (!validateMaxLength(vendor[field], 255)) {
                errorMessage = AT_MOST_255_CHAR;
            }
        }

        if (vendorCodeId !== null) {
            if (field === 'catalogCode') {
                const catalogCode = vendor[vendorCodeField][index].catalogCode;
                if (!validateRequired(catalogCode)) {
                    errorMessage = IS_REQUIRED;
                } else if (!validateExactLength(catalogCode, 4)) {
                    errorMessage = EXACTLY_4_CHAR;
                }
                if (!isOverride) {
                    if (checkCodeDuplicates(false)) return;
                }
            }

            if (field === 'royaltyCode') {
                const royaltyCode = vendor[vendorCodeField][index].royaltyCode;
                if (!validateRequired(royaltyCode)) {
                    errorMessage = IS_REQUIRED;
                } else if (!royaltyCode.startsWith('ZQ')) {
                    errorMessage = MUST_START_WITH_ZQ;
                } else if (!validateMinLength(royaltyCode, 4)) {
                    errorMessage = EXACTLY_4_TO_5_CHAR;
                } else if (!validateMaxLength(royaltyCode, 5)) {
                    errorMessage = EXACTLY_4_TO_5_CHAR;
                }
                if (!isOverride) {
                    if (checkCodeDuplicates(true)) return;
                }
            }

            if (field === 'mappingStart') {
                const mappingStartUnTrimmed = vendor[vendorCodeField][index].mappingStart;
                const mappingStart = vendor[vendorCodeField][index].mappingStart.replace(/[-\s]/g, '');
                const mappingEnd = vendor[vendorCodeField][index].mappingEnd.replace(/[-\s]/g, '');
                if (!validateRequired(mappingStart)) {
                    errorMessage = IS_REQUIRED;
                } else if (!validateDateString(mappingStartUnTrimmed)) {
                    errorMessage = INVALID_DATE;
                } else if (!validateExactLength(mappingStart, 8)) {
                    errorMessage = INCOMPLETE_DATE;
                } else if (mappingEnd && validateExactLength(mappingEnd, 8) && mappingEnd <= mappingStart) {
                    errorMessage = START_DATE_BEFORE_END_DATE;
                } else if (mappingStart.slice(-2) !== '01') {
                    errorMessage = FIRST_OF_MONTH_DATE;
                }
            }

            if (field === 'mappingEnd') {
                const mappingEndUnTrimmed = vendor[vendorCodeField][index].mappingEnd;
                const mappingEnd = vendor[vendorCodeField][index].mappingEnd.replace(/[-\s]/g, '');
                const mappingStart = vendor[vendorCodeField][index].mappingStart.replace(/[-\s]/g, '');
                if (mappingEnd && !validateExactLength(mappingEnd, 8)) {
                    errorMessage = INCOMPLETE_DATE;
                } else if (mappingEnd && !validateDateString(mappingEndUnTrimmed)) {
                    errorMessage = INVALID_DATE;
                } else if (
                    mappingStart &&
                    mappingEnd &&
                    validateExactLength(mappingStart, 8) &&
                    mappingEnd <= mappingStart
                ) {
                    errorMessage = END_DATE_AFTER_START_DATE;
                } else if (mappingEndUnTrimmed !== getLastDayOfMonth(mappingEndUnTrimmed)) {
                    errorMessage = LAST_OF_MONTH_DATE;
                }
            }

            if (field === 'serviceId') {
                const service = vendor.vendorCodeOverrides[index].serviceId?.toString();
                if (!validateRequired(service)) {
                    errorMessage = IS_REQUIRED;
                }
            }

            if (field === 'territoryId') {
                const territory = vendor.vendorCodeOverrides[index].territoryId?.toString();
                if (!validateRequired(territory)) {
                    errorMessage = IS_REQUIRED;
                }
            }

            if (
                isOverride &&
                vendor[vendorCodeField][index].catalogCode &&
                vendor[vendorCodeField][index].royaltyCode &&
                vendor[vendorCodeField][index].mappingStart.replace(/[-\s]/g, '')
            ) {
                if (checkOverrideCodeDateOverlaps()) return;
            }
        }

        setErrorMessages((prevState) => {
            const newState = { ...prevState };
            if (errorMessage === '') {
                if (vendorCodeId !== null) {
                    delete newState[`${vendorCodeField}${vendorCodeId}${field}`];
                } else {
                    delete newState[field];
                }
            } else {
                if (vendorCodeId !== null) {
                    newState[`${vendorCodeField}${vendorCodeId}${field}`] = errorMessage;
                } else {
                    newState[field] = errorMessage;
                }
            }
            return newState;
        });
    };

    const checkOverrideCodeDateOverlaps = () => {
        let overlapsFound = false;

        const codesMap: Record<
            string,
            Array<{ vendorCodeId: string; mappingStartTimestamp: number; mappingEndTimestamp: number }>
        > = {};
        const vendorCodes = [...vendor.vendorCodeOverrides];
        vendorCodes.forEach((vendorCode) => {
            const vendorCodeMappingKey = `${vendorCode.catalogCode}-${vendorCode.royaltyCode.trim()}-${
                vendorCode.serviceId
            }-${vendorCode.territoryId}`;

            if (!codesMap[vendorCodeMappingKey]) {
                codesMap[vendorCodeMappingKey] = [];
            }

            codesMap[vendorCodeMappingKey].push({
                vendorCodeId: vendorCode.vendorCodeId,
                mappingStartTimestamp: getDateAndTimestamp(
                    vendorCode.mappingStart === emptyDateYyyyMmDdMaskStr ? '' : vendorCode.mappingStart
                )[1],
                mappingEndTimestamp: getDateAndTimestamp(
                    vendorCode.mappingEnd === emptyDateYyyyMmDdMaskStr ? '' : vendorCode.mappingEnd
                )[1]
            });
        });

        // clear DATE_RANGE_OVERLAP error message before running validation
        for (const key in codesMap) {
            codesMap[key].forEach(({ vendorCodeId }) => {
                if (errorMessages[`${VENDOR_CODE_OVERRIDES}${vendorCodeId}${MAPPING_START}`] === DATE_RANGE_OVERLAP) {
                    setErrorMessages((prevState) => {
                        const newState = { ...prevState };
                        delete newState[`${VENDOR_CODE_OVERRIDES}${vendorCodeId}${MAPPING_START}`];
                        return newState;
                    });
                }
            });
        }

        for (const key in codesMap) {
            if (codesMap[key].length > 1) {
                // find all vendor code mappings that have overlapped date range
                for (let i = 0; i < codesMap[key].length; i++) {
                    const overlapVendorCodeIdSet = new Set();
                    for (let j = i + 1; j < codesMap[key].length; j++) {
                        const mapping1 = codesMap[key][i];
                        const mapping2 = codesMap[key][j];
                        if (
                            mapping1.mappingStartTimestamp <= mapping2.mappingEndTimestamp &&
                            mapping2.mappingStartTimestamp <= mapping1.mappingEndTimestamp
                        ) {
                            overlapVendorCodeIdSet.add(mapping1.vendorCodeId);
                            overlapVendorCodeIdSet.add(mapping2.vendorCodeId);
                        }
                    }
                    if (overlapVendorCodeIdSet.size > 0) {
                        overlapsFound = true;
                        setErrorMessages((prevState) => {
                            const newState = { ...prevState };
                            overlapVendorCodeIdSet.forEach((vendorCodeId) => {
                                newState[`${VENDOR_CODE_OVERRIDES}${vendorCodeId}${MAPPING_START}`] =
                                    DATE_RANGE_OVERLAP;
                            });
                            return newState;
                        });
                    }
                }
            }
        }

        return overlapsFound;
    };

    return (
        <Column spacing="500">
            {isEditMode && (
                <div className="edit-warning-alert">
                    <Alert type="warning">Edits may require reruns for past accounting periods.</Alert>
                </div>
            )}
            <Heading level={2}>{isEditMode ? 'Edit vendor' : 'Add vendor'}</Heading>
            <Column>
                <Column width="45%">
                    <AndonCord reason={'https://jira.music.amazon.dev/browse/RAD-2934'} pulled={isEditMode}>
                        <Input
                            label="Vendor name *"
                            value={vendor.vendorName}
                            onChange={(value) => handleChange('vendorName', value)}
                            onBlur={() => validateField('vendorName')}
                            error={!!errorMessages.vendorName}
                            errorMessage={errorMessages.vendorName}
                            type="text"
                            size="large"
                            placeholder="Enter label name"
                        />
                    </AndonCord>
                </Column>
                <Column width="60%">
                    <AndonCord reason={'https://jira.music.amazon.dev/browse/RAD-2934'} pulled={isEditMode}>
                        <Textarea
                            label="Description *"
                            value={vendor.description}
                            onChange={(value) => handleChange('description', value)}
                            onBlur={() => validateField('description')}
                            error={!!errorMessages.description}
                            errorMessage={errorMessages.description}
                            resize="none"
                        />
                    </AndonCord>
                </Column>
            </Column>
            <VendorCodes
                vendorCodes={vendor.vendorCodes}
                addVendorCode={addVendorCode}
                removeVendorCode={removeVendorCode}
                updateVendorCode={updateVendorCode}
                validateField={validateField}
                errorMessages={errorMessages}
            />
            <VendorCodeOverrides
                vendorCodeOverrides={vendor.vendorCodeOverrides}
                addVendorCodeOverride={addVendorCode}
                removeVendorCodeOverride={removeVendorCode}
                updateVendorCodeOverride={updateVendorCode}
                validateField={validateField}
                errorMessages={errorMessages}
            />
            <SubmissionSettings vendor={vendor} isEditMode={true} handleChange={handleChange} />
            <Row alignmentHorizontal="end">
                <Button onClick={openCancelModal} type="link" minWidth={174}>
                    Cancel
                </Button>
                <Button onClick={handleNext} disabled={!isFormValid} minWidth={174}>
                    Next
                </Button>
            </Row>
        </Column>
    );
};
